/**
 * Prompt Utility Functions
 *
 * Helper functions for working with prompt templates:
 * - Variable extraction and replacement
 * - Prompt validation
 * - Search and filtering
 */

import type { PromptTemplate, PromptCategory } from '@ctrl-enter/types';

// Re-export shared utilities from types package
export {
  replacePromptVariables,
  extractVariableNames,
  validatePromptVariables,
  getSystemTemplatesByCategory,
  getCategoriesWithCounts,
} from '@ctrl-enter/types';

/**
 * Search prompts by title or description
 */
export function searchPrompts(prompts: PromptTemplate[], query: string): PromptTemplate[] {
  if (!query.trim()) {
    return prompts;
  }

  const searchLower = query.toLowerCase();

  return prompts.filter(
    prompt =>
      prompt.title.toLowerCase().includes(searchLower) ||
      prompt.description?.toLowerCase().includes(searchLower)
  );
}

/**
 * Filter prompts by category
 */
export function filterPromptsByCategory(
  prompts: PromptTemplate[],
  category: PromptCategory
): PromptTemplate[] {
  return prompts.filter(p => p.category === category);
}

/**
 * Sort prompts by various criteria
 */
export function sortPrompts(
  prompts: PromptTemplate[],
  sortBy: 'title' | 'recent' | 'popular'
): PromptTemplate[] {
  const sorted = [...prompts];

  switch (sortBy) {
    case 'title':
      return sorted.sort((a, b) => a.title.localeCompare(b.title));

    case 'recent':
      return sorted.sort((a, b) => b.updatedAt - a.updatedAt);

    case 'popular':
      return sorted.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

    default:
      return sorted;
  }
}

/**
 * Get prompts that have variables
 */
export function getPromptsWithVariables(prompts: PromptTemplate[]): PromptTemplate[] {
  return prompts.filter(p => p.variables && p.variables.length > 0);
}

/**
 * Check if prompt content has unresolved variables
 */
export function hasUnresolvedVariables(content: string): boolean {
  const pattern = /{{(\s*[\w-]+\s*)}}/g;
  return pattern.test(content);
}

/**
 * Create a hash of content for cache keys
 * Simple hash function for suggestion cache
 */
export function hashContent(content: string): string {
  let hash = 0;
  for (let i = 0; i < content.length; i++) {
    const char = content.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Generate context hash for suggestion caching
 */
export function generateContextHash(
  screenCaptureId: string | undefined,
  recentMessages: string[]
): string {
  const content = [screenCaptureId || '', ...recentMessages].join('|');
  return hashContent(content);
}

/**
 * Check if suggestion cache is still valid
 */
export function isCacheValid(
  cacheTimestamp: number,
  maxAgeMs: number = 5 * 60 * 1000 // 5 minutes default
): boolean {
  return Date.now() - cacheTimestamp < maxAgeMs;
}
